import axios from "axios";

const API_URL = "https://lukkypro.ru/api/v1/";
const ACCESS_TOKEN_KEY = "authToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const AUTH_CHANGED_EVENT = "auth:changed";
const TOKEN_REFRESH_SKEW_MS = 60_000;

type AuthTokens = {
    access_token: string;
    refresh_token: string;
};

let refreshPromise: Promise<string | null> | null = null;
let axiosAuthConfigured = false;
let fetchAuthConfigured = false;
const nativeFetch = window.fetch.bind(window);

function notifyAuthChanged() {
    window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
}

function tokenExpiresAt(token: string): number | null {
    try {
        const payload = token.split(".")[1];
        if (!payload) return null;

        const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
        const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
        const decoded = JSON.parse(window.atob(padded)) as { exp?: number };
        return typeof decoded.exp === "number" ? decoded.exp * 1000 : null;
    } catch {
        return null;
    }
}

function tokenIsFresh(token: string): boolean {
    const expiresAt = tokenExpiresAt(token);
    return expiresAt !== null && expiresAt - Date.now() > TOKEN_REFRESH_SKEW_MS;
}

export function getStoredAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function storeAuthTokens(tokens: AuthTokens) {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
    notifyAuthChanged();
}

export function clearAuthTokens() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    notifyAuthChanged();
}

async function requestTokenRefresh(): Promise<string | null> {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
        clearAuthTokens();
        return null;
    }

    const response = await nativeFetch(`${API_URL}auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (response.status === 401 || response.status === 403) {
        clearAuthTokens();
        return null;
    }

    if (!response.ok) {
        throw new Error(`Token refresh failed with status ${response.status}`);
    }

    const tokens = await response.json() as AuthTokens;
    storeAuthTokens(tokens);
    return tokens.access_token;
}

export async function refreshAccessToken(): Promise<string | null> {
    if (!refreshPromise) {
        refreshPromise = requestTokenRefresh().finally(() => {
            refreshPromise = null;
        });
    }
    return refreshPromise;
}

export async function getValidAccessToken(forceRefresh = false): Promise<string | null> {
    const accessToken = getStoredAccessToken();
    if (!forceRefresh && accessToken && tokenIsFresh(accessToken)) {
        return accessToken;
    }

    return refreshAccessToken();
}

function isAuthEndpoint(url?: string): boolean {
    return !!url && /\/auth\/(token|register|refresh)(?:\?|$)/.test(url);
}

function isApiEndpoint(url?: string): boolean {
    return !!url && url.startsWith(API_URL);
}

export function configureAxiosAuth() {
    if (axiosAuthConfigured) return;
    axiosAuthConfigured = true;
    configureFetchAuth();

    axios.interceptors.request.use(async (config) => {
        if (!isApiEndpoint(config.url) || isAuthEndpoint(config.url)) return config;

        const token = await getValidAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    });

    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const config = error?.config as (typeof error.config & { _authRetry?: boolean }) | undefined;
            if (error?.response?.status !== 401 || !config || config._authRetry ||
                !isApiEndpoint(config.url) || isAuthEndpoint(config.url)) {
                return Promise.reject(error);
            }

            config._authRetry = true;
            const token = await refreshAccessToken();
            if (!token) return Promise.reject(error);

            config.headers.Authorization = `Bearer ${token}`;
            return axios(config);
        },
    );
}

export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
    const execute = async (token: string | null) => {
        const headers = new Headers(input instanceof Request ? input.headers : undefined);
        new Headers(init.headers).forEach((value, key) => headers.set(key, value));
        if (token) headers.set("Authorization", `Bearer ${token}`);
        const requestInput = input instanceof Request ? input.clone() : input;
        return nativeFetch(requestInput, { ...init, headers });
    };

    const response = await execute(await getValidAccessToken());
    if (response.status !== 401) return response;

    const token = await refreshAccessToken();
    if (!token) return response;

    const retryResponse = await execute(token);
    if (retryResponse.status === 401) clearAuthTokens();
    return retryResponse;
}

function configureFetchAuth() {
    if (fetchAuthConfigured) return;
    fetchAuthConfigured = true;

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
        const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
        if (!isApiEndpoint(url) || isAuthEndpoint(url)) {
            return nativeFetch(input, init);
        }
        return authFetch(input, init);
    };
}

export const authChangedEvent = AUTH_CHANGED_EVENT;
